import { useEffect, useState, useCallback } from 'react'
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { showLoading } from 'services/util'
import { history } from 'services/history.js'

import { urlQueryParser } from 'services/util'
import { getAuctionStatus } from '../util/dateString'

import { adminGetAuctionByCode, collaboratorGetAuctionByCode } from './auctionsadminutil'

import adminCss from './sass/adminpage.module.scss'
import themeCss from '../components/sass/theme.module.scss'

import TabNav from 'sites/auctions/components/TabNav'
import AdminAuctionDetail from './AdminAuctionDetail'
import AdminAuctionItems from './AdminAuctionItems'
import PreviewPublish from './PreviewPublish'
import AuctionDetailsAlert from './AuctionDetailsAlert'
import AdminBreadcrumbs from './AdminBreadcrumbs'
import LiveAttendeeView from './UserDetail/LiveAttendeeView'
import PreAttendees from './UserDetail/PreAttendees'
import ClosedAttendeeView from './ClosedAuction/ClosedAttendeeView'
import { setSessionItem } from '../components/utils'
 
export default function AdminAuctionTabs(props) {
  
  const [tabPanels, setTabPanels] = useState([])
  const [goToIndex, setGoToIndex] = useState(0)

  // stores if the auction values have been edited but not saved
  const [unsavedAuction, setUnsavedAuction] = useState(false)

  //tab headings never change title
  const tabTitles = ['Auction Details', 'Items & Donations', 'View Attendees']
  //to refresh query on changes from the Status update
  const queryClient = useQueryClient()

  const { auctionId: auctionCode } = urlQueryParser(props.location.search)

  // load auction data by code, so it will work for deep-linking
  const { data: auctionDetail, error, isLoading } = useQuery({
    queryKey: ["adminAuctionTabDetail", auctionCode],
    queryFn: async () => {
      return await adminGetAuctionByCode(auctionCode)
    },
    staleTime: 0
  })
  
  const liveStatus = getAuctionStatus(auctionDetail?.StartTime, auctionDetail?.EndTime)

  // scroll to the top of the page smoothly
  useEffect(() => {
    window.scrollTo({top: 0, left: 0, behavior: 'smooth' })
  }, [])

  const refreshAdminAuctionQuery = useCallback(() => {
    queryClient.invalidateQueries(["adminAuctionTabDetail"])
  }, [queryClient])

  // poll for check in status every 5 seconds if auction is running
  useEffect(() => {
    let timer

    function clearTimer() {
      setSessionItem('historyProps', props.history)
      
      if (timer) {
        console.log('clearing timer')
        clearInterval(timer)
      }

    }

    async function refreshAuctionDetail() {
      if (auctionDetail && auctionCode) {
        console.log('will refresh auction detail')

        timer = setInterval(async () => {
          refreshAdminAuctionQuery()
          console.log('refreshing auction detail')
        }, 1000*5)
      }
      return
    }

    refreshAuctionDetail()

    return clearTimer

  }, [auctionDetail, auctionCode, refreshAdminAuctionQuery, props.history])

  useEffect(() => {
    switch(liveStatus) {
      case "ended":
        setTabPanels([<AdminAuctionDetail history={props.history} auction={auctionDetail} refreshAdminAuctionQuery={refreshAdminAuctionQuery} setGoToIndex={setGoToIndex} 
                        unsavedAuction={unsavedAuction} setUnsavedAuction={setUnsavedAuction} />,
                      <AdminAuctionItems auction={auctionDetail} refreshAdminAuctionQuery={refreshAdminAuctionQuery} hasEnded={true} />,
                      <ClosedAttendeeView auction={auctionDetail} />])                      
        break;
      case "started":
        let startedPanels = [<AdminAuctionDetail history={props.history} auction={auctionDetail} refreshAdminAuctionQuery={refreshAdminAuctionQuery} setGoToIndex={setGoToIndex} 
                        unsavedAuction={unsavedAuction} setUnsavedAuction={setUnsavedAuction} />,
                      <AdminAuctionItems auction={auctionDetail} refreshAdminAuctionQuery={refreshAdminAuctionQuery} />]
        // Attendee tab depends on if auction is in preview or not
        if (auctionDetail?.Status === "PREVIEW") startedPanels.push(<PreAttendees />)
        else startedPanels.push(<LiveAttendeeView auction={auctionDetail} />)
        setTabPanels(startedPanels)
        break;
      case "before":
        
        let beforePanels = [<AdminAuctionDetail history={props.history} auction={auctionDetail} refreshAdminAuctionQuery={refreshAdminAuctionQuery} setGoToIndex={setGoToIndex} 
            unsavedAuction={unsavedAuction} setUnsavedAuction={setUnsavedAuction} />,
          <AdminAuctionItems auction={auctionDetail} refreshAdminAuctionQuery={refreshAdminAuctionQuery} />,
        ]
        
        // assign attendee tab based on auction public status
        if (auctionDetail?.Status === 'PUBLIC') beforePanels.push(<LiveAttendeeView auction={auctionDetail} />)
        else beforePanels.push(<PreAttendees />)

        setTabPanels(beforePanels)
        break;
      default:
        setTabPanels([<AdminAuctionDetail history={props.history} auction={auctionDetail} refreshAdminAuctionQuery={refreshAdminAuctionQuery} setGoToIndex={setGoToIndex} 
                        unsavedAuction={unsavedAuction} setUnsavedAuction={setUnsavedAuction} />,
                      <AdminAuctionItems auction={auctionDetail} refreshAdminAuctionQuery={refreshAdminAuctionQuery} />,
                      <PreAttendees />])
      }

  }, [liveStatus, auctionDetail, auctionCode, props.history, refreshAdminAuctionQuery, unsavedAuction])

  //use effect for query results
  useEffect(() => {
    showLoading(isLoading)

    if (error || (auctionDetail && auctionDetail.error)) {
      console.log('Error!', error || auctionDetail.error)
    }
  }, [auctionDetail, error, isLoading])

  //wait for the page to load and have the correct number of tabs to match the tab labels
  if (tabPanels.length < 2) return

  return (
    <div>
      <AdminBreadcrumbs auctionDetail={auctionDetail} liveStatus={liveStatus} />

        <div className={adminCss['admin-auction-page-header']}>
          {auctionDetail?.Status === 'PREVIEW' ?
            <h3>{auctionDetail?.Title}</h3> :
            <button
            onClick ={(e) => {history.push({
              pathname: '/auctionhome',
              search: `?auctionId=${auctionDetail.AuctionCode}`,
            })}}
            className={`${adminCss['admin-auction-page-header-button']} ${themeCss['auction-title-link']}`}
          >{auctionDetail?.Title}</button>
          }
          <PreviewPublish auctionDetail={auctionDetail} liveStatus={liveStatus} refreshAdminAuctionQuery={refreshAdminAuctionQuery} />
        </div>

        <AuctionDetailsAlert liveStatus={liveStatus} auctionDetail={auctionDetail} />

        <div className={adminCss['admin-auction-page-tabnav']} >
          <TabNav titles={tabTitles} panels={tabPanels} goToIndex={goToIndex} unsavedAuction={unsavedAuction} />
        </div>
    </div>
  )
}